/**
 * Animation utility functions
 */

/**
 * Normalize transform origin value
 * Transform origin should be in X Y format (first = X/horizontal, second = Y/vertical)
 * CSS keywords: left/center/right (X) and top/center/bottom (Y)
 */
export function normalizeTransformOrigin(value: string): string {
  const parts = value.trim().split(/\s+/)

  if (parts.length === 1) {
    // Single value - assume it applies to both X and Y
    return `${parts[0]} ${parts[0]}`
  }

  if (parts.length === 2) {
    // Two values - already in correct format
    return `${parts[0]} ${parts[1]}`
  }

  // More than two values - take first two
  return `${parts[0]} ${parts[1]}`
}

/**
 * Apply easing to a progress value t (0–1). Returns eased t in [0, 1].
 * Uses cubic easing (same feel as CSS ease-in / ease-out / ease-in-out).
 */
export function easeKeyframeProgress(
  t: number,
  ease: 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out'
): number {
  if (ease === 'linear') return t
  if (ease === 'ease-in') return t * t * t
  if (ease === 'ease-out') return 1 - Math.pow(1 - t, 3)
  if (ease === 'ease-in-out') return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
  return t
}

/**
 * Interpolate a keyframe property at a given progress (0–100).
 * Keyframes must be sorted by `at`. Optional easing is applied per segment (from keyframe N to N+1).
 */
export function getKeyframeValue(
  keyframes: Array<{ at: number; rotate?: number; scale?: number; translateX?: number; translateY?: number; ease?: 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out' }>,
  progressPercent: number,
  key: 'rotate' | 'scale' | 'translateX' | 'translateY',
  defaultEase?: 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out',
): number | undefined {
  const withValue = keyframes.filter((k) => k[key] !== undefined)
  if (withValue.length === 0) return undefined
  if (withValue.length === 1) return (withValue[0] as any)[key]
  if (progressPercent <= (withValue[0] as any).at) return (withValue[0] as any)[key]
  if (progressPercent >= (withValue[withValue.length - 1] as any).at) return (withValue[withValue.length - 1] as any)[key]
  let i = 0
  while (i < withValue.length - 1 && (withValue[i + 1] as any).at < progressPercent) i++
  const a = withValue[i] as any
  const b = withValue[i + 1] as any
  const rawT = (progressPercent - a.at) / (b.at - a.at)
  const ease = a.ease ?? defaultEase ?? 'linear'
  const t = easeKeyframeProgress(rawT, ease)
  return a[key] + (b[key] - a[key]) * t
}
